import { useEffect, useState } from 'react';
import { auth, storage } from '../../../Config/firebase.js';
import useStoreData from './StoreData.js';
import useModal from './Modal.js';
import useAIGeneration from './aiGeneration.js';
import useMeshyDownloader from './MeshyDownloader.js';

const useStoreActions = (stores, setStores, selectedStore, setSelectedStore, items, setItems, selectedItem, setSelectedItem) => {
  const storeData = useStoreData();
  const modal = useModal();
  const aiGeneration = useAIGeneration();
  const meshyDownloader = useMeshyDownloader(stores, setStores, selectedStore, setItems, selectedItem, setSelectedItem, storeData.saveStore);
  
  // State for tracking uploading items
  const [uploadingItems, setUploadingItems] = useState([]);

  const loadUserStores = async () => {
    const allStores = await storeData.loadUserStores();
    setStores(allStores);
    return allStores;
  };

  const handleCreateStore = async (storeName) => {
    const storeId = Date.now().toString();
    const newStore = {
      id: storeId,
      name: storeName,
      items: [],
      shareLink: `https://xyronis.xyz/mr-demo?storeId=${storeId}`,
      createdAt: new Date().toISOString(),
    };
    
    try {
      await storeData.saveStore(newStore);
      
      const updatedStores = [...stores, newStore];
      setStores(updatedStores);
      setSelectedStore(newStore);
      setItems([]);
      
    } catch (error) {
      console.error('❌ Failed to create store:', error);
    }
  };

  const handleRenameStore = async () => {
    if (!modal.renameStoreName.trim()) {
      alert('Please enter a store name.');
      return;
    }
    
    const updatedStore = { ...selectedStore, name: modal.renameStoreName };
    
    try {
      await storeData.saveStore(updatedStore);
      
      const updatedStores = stores.map((store) =>
        store.id === selectedStore.id ? updatedStore : store
      );
      
      setStores(updatedStores);
      setSelectedStore(updatedStore);
      modal.closeRename();
    } catch (error) {
      console.error('❌ Failed to rename store:', error);
    }
  };

  const handleUpload = async (uploadData) => {
    const user = auth.currentUser;
    modal.setError(null);
    
    // Create uploading item entry
    const uploadingItemId = Date.now().toString();
    const uploadingItem = {
      id: uploadingItemId,
      itemName: uploadData.itemName,
      progress: 0
    };
    
    // Add to uploading items (unless editing existing item)
    if (!modal.editItem) {
      setUploadingItems(prev => [...prev, uploadingItem]);
    }

    try {
      
      if (uploadData.mode === 'ai') {
        try {
          // For AI generation, we'll rely on the existing progress from aiGeneration hook
          // but still show the item in uploading state
          const newItem = await aiGeneration.generateModelFromImages(
            uploadData.images,
            uploadData.itemName,
            uploadData.dimensions,
            user
          );

          const itemWithCategory = {
            ...newItem,
            category: uploadData.category,
            id: modal.editItem ? modal.editItem.id : newItem.id,
          };

          await updateStoreWithNewItem(itemWithCategory);
          
          // Remove from uploading items after completion
          if (!modal.editItem) {
            setUploadingItems(prev => prev.filter(item => item.id !== uploadingItemId));
          }
        } catch (meshyError) {
          throw meshyError;
        }
      } else {
        let glbUrl, imageUrl;

        // Update progress to 25%
        if (!modal.editItem) {
          setUploadingItems(prev => prev.map(item => 
            item.id === uploadingItemId ? { ...item, progress: 25 } : item
          ));
        }

        if (uploadData.glbFile instanceof File) {
          const glbRef = storage.ref(`glb-files/${user.uid}/${uploadData.glbFile.name}`);
          const uploadTask = glbRef.put(uploadData.glbFile);
          
          // Track GLB upload progress
          await new Promise((resolve, reject) => {
            uploadTask.on('state_changed',
              (snapshot) => {
                if (!modal.editItem) {
                  const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 50) + 25; // 25-75%
                  setUploadingItems(prev => prev.map(item => 
                    item.id === uploadingItemId ? { ...item, progress } : item
                  ));
                }
              },
              reject,
              () => resolve(uploadTask.snapshot.ref.getDownloadURL())
            );
          }).then(url => glbUrl = url);
        } else {
          glbUrl = uploadData.glbFile;
        }

        // Update progress to 75%
        if (!modal.editItem) {
          setUploadingItems(prev => prev.map(item => 
            item.id === uploadingItemId ? { ...item, progress: 75 } : item
          ));
        }

        if (uploadData.previewImage instanceof File) {
          const imageRef = storage.ref(`images/${user.uid}/${uploadData.previewImage.name}`);
          const imageUploadTask = imageRef.put(uploadData.previewImage);
          
          // Track image upload progress  
          await new Promise((resolve, reject) => {
            imageUploadTask.on('state_changed',
              (snapshot) => {
                if (!modal.editItem) {
                  const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 20) + 75; // 75-95%
                  setUploadingItems(prev => prev.map(item => 
                    item.id === uploadingItemId ? { ...item, progress } : item
                  ));
                }
              },
              reject,
              () => resolve(imageUploadTask.snapshot.ref.getDownloadURL())
            );
          }).then(url => imageUrl = url);
        } else {
          imageUrl = uploadData.previewImage;
        }

        const newItem = {
          id: modal.editItem ? modal.editItem.id : Date.now().toString(),
          itemName: uploadData.itemName,
          glbUrl,
          imageUrl,
          timestamp: new Date().toISOString(),
          category: uploadData.category,
        };

        // Update progress to 95% before final save
        if (!modal.editItem) {
          setUploadingItems(prev => prev.map(item =>
            item.id === uploadingItemId ? { ...item, progress: 95 } : item
          ));
        }

        await updateStoreWithNewItem(newItem);

        // Remove uploading item immediately after real item is added to avoid duplicates
        if (!modal.editItem) {
          setUploadingItems(prev => prev.filter(item => item.id !== uploadingItemId));
        }
      }
    } catch (error) {
      console.error('❌ Upload failed:', error.message);
      
      // Remove failed upload from uploading items
      if (!modal.editItem) {
        setUploadingItems(prev => prev.filter(item => item.id !== uploadingItemId));
      }
      
      aiGeneration.resetGeneration();
    }
  };

  const updateStoreWithNewItem = async (newItem) => {
    let updatedItems;
    if (modal.editItem) {
      updatedItems = items.map(item => item.id === modal.editItem.id ? newItem : item);
    } else {
      updatedItems = [...items, newItem];
    }

    setItems(updatedItems);
    setSelectedItem(newItem);

    const updatedStore = {
      ...selectedStore,
      items: updatedItems,
      updatedAt: new Date().toISOString()
    };
    
    const updatedStores = stores.map((store) =>
      store.id === selectedStore.id ? updatedStore : store
    );
    
    setStores(updatedStores);
    setSelectedStore(updatedStore);
    
    await storeData.saveStore(updatedStore);
    modal.setEditItem(null);
  };

  const handleRemoveStore = async () => {
    if (!modal.itemToRemove) return;
    
    try {
      await storeData.deleteStore(modal.itemToRemove.id);
      
      const updatedStores = stores.filter(store => store.id !== modal.itemToRemove.id);
      setStores(updatedStores);
      
      if (selectedStore && selectedStore.id === modal.itemToRemove.id) {
        setSelectedStore(updatedStores.length > 0 ? updatedStores[0] : null);
        setItems(updatedStores.length > 0 ? updatedStores[0].items || [] : []);
      }
      
      modal.closeRemove();
    } catch (error) {
      console.error('❌ Failed to remove store:', error);
    }
  };

  const handleRemoveItem = async () => {
    if (!modal.itemToRemove) return;
    
    const updatedItems = items.filter(item => item.id !== modal.itemToRemove.id);
    setItems(updatedItems);
    
    if (selectedItem && selectedItem.id === modal.itemToRemove.id) {
      setSelectedItem(updatedItems.length > 0 ? updatedItems[0] : null);
    }
    
    const updatedStore = {
      ...selectedStore,
      items: updatedItems,
      updatedAt: new Date().toISOString()
    };
    
    const updatedStores = stores.map((store) =>
      store.id === selectedStore.id ? updatedStore : store
    );
    
    setStores(updatedStores);
    setSelectedStore(updatedStore);
    
    try {
      await storeData.saveStore(updatedStore);
      modal.closeRemove();
    } catch (error) {
      console.error('❌ Failed to remove item:', error);
    }
  };

  const handleLogout = () => {
    auth.signOut();
  };

  const copyLinkToClipboard = async (store) => {
    try {
      document.activeElement.blur();
      window.focus();
      if (!document.hasFocus()) {
        console.warn('Document not focused, attempting to copy anyway');
      }
      await navigator.clipboard.writeText(store.shareLink);
    } catch (error) {
      console.error('❌ Failed to copy link:', error);
    }
  };

  const generateItemShareLink = (glbUrl) => {
    const baseUrl = window.location.origin;
    const encodedUrl = encodeURIComponent(glbUrl);
    return `${baseUrl}/mr-demo?glb=${encodedUrl}`;
  };

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        loadUserStores();
      } else {
        setStores([]);
      }
    });

    return () => unsubscribe();
  }, []);

  return {
    ...modal,
    ...aiGeneration,
    ...meshyDownloader,
    uploadingItems,
    handleCreateStore,
    handleRenameStore,
    handleUpload,
    handleRemoveStore,
    handleRemoveItem,
    handleLogout,
    copyLinkToClipboard,
    generateItemShareLink,
    loadUserStores,
    loadStoreById: storeData.loadStoreById,
  };
};

export default useStoreActions;